import { DateTime } from "luxon";

import { ITileCacheSeedOperation, TileCacheSeedOperationStatus } from "../types";
import { IIndexedDbTileCacheEntry, IIndexedDbTileCacheOptions, IIndexedDbTileOptions, TileCacheSeedStrategy } from "./types";
import { IndexedDbLogic } from "./indexedDbLogic";
import { DEFAULT_DATABASE_VERSION } from "../../../../ServiceWorker/TileCache/indexedDb/constants";
import { ITileCoordinates } from "../../tileCoordinates";


export class Logic {

    public static readonly BGFETCH_ID_PREFIX: string = 'tileCacheSeed-';

    private static readonly OP_FINAL_STATES = [TileCacheSeedOperationStatus.Cancelled, TileCacheSeedOperationStatus.Failed, TileCacheSeedOperationStatus.Succeeded];

    /**
     * Creates an internal tile url from the url template from IIndexedDbTileCacheOptions
     *
     * It keeps the sub-domain placeholder to provide unique database entries while seeding from multiple sub-domains.
     */
    public static createInternalTileUrl(urlTemplate: string, tileCoordinates: ITileCoordinates): string {
        return urlTemplate!
            .split(/{x}/).join(tileCoordinates.x.toString())
            .split(/{y}/).join(tileCoordinates.y.toString())
            .split(/{z}/).join(tileCoordinates.z.toString());
    }

    /**
     * Creates a real tile url from the url template from IIndexedDbTileCacheOptions
     */
    public static createTileUrl(urlTemplate: string, urlSubdomains: string[], tileCoordinates: ITileCoordinates): string {
        const randomSubDomain: string = urlSubdomains![Math.floor(Math.random() * urlSubdomains!.length)] ?? '';
        return Logic.createInternalTileUrl(urlTemplate, tileCoordinates)
            .split(/{s}/).join(randomSubDomain);
    }

    public static async cacheTileFetchResult(
        options: IIndexedDbTileCacheOptions, 
        contentType: string, 
        data: ArrayBuffer, 
        url: string
    ): Promise<IIndexedDbTileCacheEntry> {

        return new Promise((resolve, reject) => {

            const dbRequest = IndexedDbLogic.openDatabase(options.databaseName!, DEFAULT_DATABASE_VERSION, reject);

            const tileCacheEntry: IIndexedDbTileCacheEntry = {
                contentType,
                data,
                url: url,
                timestamp: Date.now()
            };

            dbRequest.addEventListener("success", (dbEvent: any) => {
                const database: IDBDatabase = dbEvent.target.result;

                const query = database.transaction([options.tileObjectStoreName!], "readwrite")
                    .objectStore(options.tileObjectStoreName!).put(tileCacheEntry);

                query.addEventListener("success", (_event) => {
                    resolve(tileCacheEntry);
                });

                query.addEventListener("error", (event: any) => {
                    reject(event.target.error);
                });
            });

        });
    }

    /**
     * Filter given URLs based on whether the cache already contains a recent version of tiles.
     * @param options 
     * @param tileUrls 
     */
    public static filterCachedTileCoords(
        options: IIndexedDbTileCacheOptions, 
        coords: ITileCoordinates[],
        urlTemplate: string,
    ): Promise<ITileCoordinates[]> {

        if (options.seedStrategy === TileCacheSeedStrategy.All)
            return Promise.resolve(coords);

        const refetchCutoffTimestamp = Logic.subtractDaysFromTimestamp(Date.now(), options.tileRefetchAgeDays);

        return new Promise((resolve, reject) => {

            const dbRequest = IndexedDbLogic.openDatabase(options.databaseName!, DEFAULT_DATABASE_VERSION, reject);

            dbRequest.addEventListener("success", (dbEvent: any) => {
                const database: IDBDatabase = dbEvent.target.result;

                const query = database.transaction([options.tileObjectStoreName!], "readwrite")
                    .objectStore(options.tileObjectStoreName!).getAll();

                query.addEventListener("success", (reqEvent: any) => {
                    const cachedUrls: string[] = (reqEvent.target.result as IIndexedDbTileCacheEntry[]).filter(e => e.timestamp >= refetchCutoffTimestamp).map(e => e.url);
                    const coordsToFetch = coords.filter(c => !cachedUrls.includes(Logic.createInternalTileUrl(urlTemplate, c)));
                    resolve(coordsToFetch);
                });

                query.addEventListener("error", (event: any) => {
                    reject(event.target.error);
                });
            });

        });

    }

    public static createOp(
        options: IIndexedDbTileOptions, 
        op: ITileCacheSeedOperation,
    ): Promise<void> {

        return new Promise((resolve, reject) => {

            const dbRequest = IndexedDbLogic.openDatabase(options.databaseName!, DEFAULT_DATABASE_VERSION, reject);

            dbRequest.addEventListener("success", (dbEvent: any) => {
                const database: IDBDatabase = dbEvent.target.result;

                const query = database.transaction([options.operationObjectStoreName!], "readwrite")
                    .objectStore(options.operationObjectStoreName!).add(op);

                query.addEventListener("success", (_event) => {
                    resolve();
                });

                query.addEventListener("error", (event: any) => {
                    reject(event.target.error);
                });
            });

        });

    }

    public static getOp(
        options: IIndexedDbTileOptions, 
        id: string
    ): Promise<ITileCacheSeedOperation | null> {

        return new Promise((resolve, reject) => {

            const dbRequest = IndexedDbLogic.openDatabase(options.databaseName!, DEFAULT_DATABASE_VERSION, reject);

            dbRequest.addEventListener("success", (dbEvent: any) => {
                const database: IDBDatabase = dbEvent.target.result;

                const query = database.transaction([options.operationObjectStoreName!], "readwrite")
                    .objectStore(options.operationObjectStoreName!).get(id);

                query.addEventListener("success", (reqEvent: any) => {
                    const op: ITileCacheSeedOperation = reqEvent.target.result as ITileCacheSeedOperation;
                    resolve(op ?? null);
                });

                query.addEventListener("error", (event: any) => {
                    reject(event.target.error);
                });
            });

        });

    }

    public static async updateOpStatus(
        options: IIndexedDbTileOptions, 
        id: string,
        status: TileCacheSeedOperationStatus,
        error?: Error | null,
        downloadedBytes?: number | null,
        downloadedTotal?: number | null
    ): Promise<{ didChange: boolean, op: ITileCacheSeedOperation | null }> {

        return new Promise((resolve, reject) => {

            const dbRequest = IndexedDbLogic.openDatabase(options.databaseName!, DEFAULT_DATABASE_VERSION, reject);

            dbRequest.addEventListener("success", (dbEvent: any) => {
                const database: IDBDatabase = dbEvent.target.result;

                const cursorRequest = database.transaction([options.operationObjectStoreName!], "readwrite")
                    .objectStore(options.operationObjectStoreName!)
                    .openCursor(id);

                var op: ITileCacheSeedOperation | null =  null;

                //Update indisde cursor to ensure atomicity & avoid race-conditions
                cursorRequest.addEventListener("success", () => {
                    let cursor = cursorRequest.result;

                    if (!cursor) {
                        resolve({ didChange: false, op });
                        return;
                    }

                    op = cursor.value as ITileCacheSeedOperation;

                    if (Logic.OP_FINAL_STATES.indexOf(op.status) >= 0) {
                        cursor.continue();
                        resolve({ didChange: false, op });
                        return;
                    }

                    op.status = status;
                    op.error = error || null;
                    
                    if (op.status === TileCacheSeedOperationStatus.InProgress && !!downloadedBytes)
                        op.downloadedBytes = downloadedBytes;

                    if (op.status === TileCacheSeedOperationStatus.InProgress && !!downloadedTotal)
                        op.downloadedTotal = downloadedTotal;

                    cursor.update(op);
                    cursor.continue();

                    resolve({ didChange: true, op });
                });

                cursorRequest.addEventListener("error", (event: any) => {
                    reject(event.target.error);
                });
            });

        });

    }

    public static getOps(
        options: IIndexedDbTileOptions
    ): Promise<ITileCacheSeedOperation[]> {

        return new Promise((resolve, reject) => {

            const dbRequest = IndexedDbLogic.openDatabase(options.databaseName!, DEFAULT_DATABASE_VERSION, reject);

            dbRequest.addEventListener("success", (dbEvent: any) => {
                const database: IDBDatabase = dbEvent.target.result;
                
                const results: ITileCacheSeedOperation[] = [];

                const cursorRequest = database.transaction([options.operationObjectStoreName!], "readonly").objectStore(options.operationObjectStoreName!)
                    .index('idx_startedAt')
                    .openCursor(null, 'prev');

                cursorRequest.addEventListener("success", () => {
                    let cursor = cursorRequest.result;

                    if (cursor) {
                        const op = cursor.value as ITileCacheSeedOperation;
                        results.push(op);
                        cursor.continue();
                    }
                    else {
                        resolve(results);
                    }
                });

                cursorRequest.addEventListener("error", (event: any) => {
                    reject(event.target.error);
                });
            });

        });

    }

    public static async getNumInProgressOps(
        options: IIndexedDbTileCacheOptions
    ): Promise<number> {

        const ops = await this.getOps(options);
        const inProgressOps = ops.filter(op => op.status === TileCacheSeedOperationStatus.InProgress);
        return inProgressOps.length;
    }

    public static deleteOp(
        options: IIndexedDbTileOptions,
        id: string
    ): Promise<void> {

        return new Promise((resolve, reject) => {

            const dbRequest = IndexedDbLogic.openDatabase(options.databaseName!, DEFAULT_DATABASE_VERSION, reject);

            dbRequest.addEventListener("success", (dbEvent: any) => {
                const database: IDBDatabase = dbEvent.target.result;

                const query = database.transaction([options.operationObjectStoreName!], "readwrite")
                    .objectStore(options.operationObjectStoreName!).delete(id);

                query.addEventListener("success", () => {
                    resolve();
                });

                query.addEventListener("error", (event: any) => {
                    reject(event.target.error);
                });
            });

        });

    }

    public static cullOps(
        options: IIndexedDbTileOptions,
        seederId: string,
        currentDate: DateTime,
        cancelPredicate?: (op: ITileCacheSeedOperation) => boolean
    ): Promise<void> {

        const cutOffDate = currentDate.minus({ days: options.operationHistoryMaxAgeDays! });

        return new Promise((resolve, reject) => {

            const dbRequest = IndexedDbLogic.openDatabase(options.databaseName!, DEFAULT_DATABASE_VERSION, reject);

            dbRequest.addEventListener("success", (dbEvent: any) => {
                const database: IDBDatabase = dbEvent.target.result;
                
                const objectStore = database.transaction([options.operationObjectStoreName!], "readwrite").objectStore(options.operationObjectStoreName!);
                let cursorRequest = objectStore.openCursor();

                cursorRequest.addEventListener("success", () => {
                    let cursor = cursorRequest.result;

                    if (cursor) {

                        const op = cursor.value as ITileCacheSeedOperation;

                        if (Logic.opCullPredicate(op, cutOffDate) === true) {
                            cursor.delete();
                        }
                        else if (cancelPredicate && op.seederId === seederId && op.status === TileCacheSeedOperationStatus.InProgress && cancelPredicate(op) === true) {
                            op.status = TileCacheSeedOperationStatus.Cancelled;
                            cursor.update(op);
                        }

                        // Move to the next record
                        cursor.continue();
                    }
                    else {
                        resolve();
                    }
                });

                cursorRequest.addEventListener("error", (event: any) => {
                    reject(event.target.error);
                });
            });

        });

    }

    private static readonly cullableOpStatuses = [TileCacheSeedOperationStatus.Cancelled, TileCacheSeedOperationStatus.Failed];

    private static opCullPredicate = (op: ITileCacheSeedOperation, cutOff: DateTime): boolean => {
        const startedAt = DateTime.fromMillis(op.startedAt);
        return (!op.startedAt || cutOff > startedAt) && Logic.cullableOpStatuses.indexOf(op.status) >= 0;
    }

    public static subtractDaysFromTimestamp(timestamp: number, daysToSubtract?: number): number {
        if (!daysToSubtract)
            return timestamp;

        const subMillis = (24*60*60*1000) * daysToSubtract;
        return timestamp - subMillis;
    }

}